/**
 * Fire Perimeter Overlay
 *
 * Adds an on-demand mapbox layer for any fire's uploaded perimeter. Driven
 * entirely by window CustomEvents so any UI (right-click menu, the
 * Perimeters tab, future bulk-show tools) can toggle perimeters without
 * needing a map reference.
 *
 * Events:
 *   show-fire-perimeter  detail: { fireNumber: string, perimeterId?: string,
 *                                  geometry?: GeoJSON.Geometry,
 *                                  flyTo?: boolean }
 *     - If `geometry` is provided, draw it directly (caller already has it).
 *     - Otherwise fetch /api/perimeters/current/:fireNumber and draw that.
 *     - The newest (current) perimeter is fetched by default.
 *     - flyTo defaults to true for menu invocations; pass false from the
 *       tab UI if you want the viewport to stay put.
 *
 *   hide-fire-perimeter  detail: { fireNumber: string, perimeterId?: string }
 *     - Removes the overlay for that fire / perimeter id.
 *
 *   toggle-fire-perimeter  detail: { fireNumber: string, perimeterId?: string,
 *                                    geometry?: GeoJSON.Geometry }
 *     - Show if not currently rendered, hide if rendered. Used by the menu.
 *
 * Mapbox source/layer IDs are keyed by `perimeterId` (when known, used by the
 * tab toggle UX for historic perimeters) and otherwise by `fireNumber`.
 *
 * Designed so Franco can: swap the fetcher, change the styling, change the
 * key strategy — all without touching the menu code or the tab UI.
 */
import type mapboxgl from 'mapbox-gl';
import { getCurrentPerimeter } from '../../services/perimeterApiService';

export interface ShowPerimeterDetail {
  fireNumber: string;
  perimeterId?: string;
  geometry?: GeoJSON.Geometry;
  flyTo?: boolean;
  // Optional UI hints forwarded to the Layer Drawer entry so users can
  // distinguish multiple perimeters of the same fire (dates, areas).
  label?: string;
  description?: string;
}

export interface HidePerimeterDetail {
  fireNumber: string;
  perimeterId?: string;
}

let _registered = false;
let _activeMap: mapboxgl.Map | null = null;
const _active = new Map<string, string>(); // overlayKey → sourceId

const STYLE = {
  fillColor: '#ff7300',
  fillOpacity: 0.25,
  lineColor: '#ff5500',
  lineWidth: 2,
};

function overlayKey(d: { fireNumber: string; perimeterId?: string }): string {
  return d.perimeterId ?? `current:${d.fireNumber}`;
}

function sourceIdFor(key: string): string {
  // Mapbox source IDs must be valid CSS-ish; replace anything risky.
  return `fire-perimeter--${key.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
}

function bboxOfGeometry(geom: GeoJSON.Geometry): [number, number, number, number] | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const walk = (c: any): void => {
    if (typeof c[0] === 'number') {
      if (c[0] < minX) minX = c[0];
      if (c[0] > maxX) maxX = c[0];
      if (c[1] < minY) minY = c[1];
      if (c[1] > maxY) maxY = c[1];
    } else {
      for (const x of c) walk(x);
    }
  };
  if ('coordinates' in geom) walk(geom.coordinates);
  if (minX === Infinity) return null;
  return [minX, minY, maxX, maxY];
}

function addOverlay(
  map: mapboxgl.Map,
  key: string,
  geometry: GeoJSON.Geometry,
  flyTo: boolean,
): void {
  const sourceId = sourceIdFor(key);
  if (map.getSource(sourceId)) return; // already drawn

  map.addSource(sourceId, {
    type: 'geojson',
    data: { type: 'Feature', properties: {}, geometry } as GeoJSON.Feature,
  });
  map.addLayer({
    id: `${sourceId}-fill`,
    source: sourceId,
    type: 'fill',
    paint: { 'fill-color': STYLE.fillColor, 'fill-opacity': STYLE.fillOpacity },
  });
  map.addLayer({
    id: `${sourceId}-line`,
    source: sourceId,
    type: 'line',
    paint: { 'line-color': STYLE.lineColor, 'line-width': STYLE.lineWidth },
  });

  _active.set(key, sourceId);

  if (flyTo) {
    const bbox = bboxOfGeometry(geometry);
    if (bbox) {
      map.fitBounds(
        [[bbox[0], bbox[1]], [bbox[2], bbox[3]]],
        { padding: 60, maxZoom: 13, duration: 800 },
      );
    }
  }
}

function removeOverlay(map: mapboxgl.Map, key: string): void {
  const sourceId = _active.get(key);
  if (!sourceId) return;
  if (map.getLayer(`${sourceId}-fill`)) map.removeLayer(`${sourceId}-fill`);
  if (map.getLayer(`${sourceId}-line`)) map.removeLayer(`${sourceId}-line`);
  if (map.getSource(sourceId)) map.removeSource(sourceId);
  _active.delete(key);
}

export function isPerimeterShown(d: { fireNumber: string; perimeterId?: string }): boolean {
  return _active.has(overlayKey(d));
}

/**
 * Returns the set of overlay keys currently drawn on the map. Used by the
 * Perimeters tab on mount to hydrate its button labels — without this,
 * closing and re-opening the wildfire detail modal would leave the polygon
 * drawn but the buttons reading "Show on Map" because the React hook lost
 * its event-derived state.
 *
 * Key shape:  `current:<fireNumber>` for the newest perimeter, or the
 * perimeter UUID for a historic one.
 */
export function getShownOverlayKeys(): string[] {
  return Array.from(_active.keys());
}

/**
 * Wire the overlay event listeners to the given map.
 *
 * Why the module-level `_activeMap` (and not a closure-captured `map`):
 * React StrictMode + dev HMR cause this function to be called more than
 * once with potentially different map instances. We always keep `_activeMap`
 * pointing at the most recent one, and the listeners read from that ref —
 * so a stale map from an unmounted EasyMap never silently swallows clicks.
 */
export function registerFirePerimeterOverlay(map: mapboxgl.Map): void {
  _activeMap = map;
  if (_registered) return;
  _registered = true;

  const onShow = async (ev: Event) => {
    const detail = (ev as CustomEvent<ShowPerimeterDetail>).detail;
    if (!detail?.fireNumber) return;
    if (!_activeMap) {
      console.warn('[firePerimeterOverlay] show event fired but no map registered');
      return;
    }
    const key = overlayKey(detail);
    const flyTo = detail.flyTo !== false;

    let geometry = detail.geometry;
    if (!geometry) {
      try {
        const record = await getCurrentPerimeter(detail.fireNumber);
        if (!record) {
          console.warn(`[firePerimeterOverlay] No perimeter on file for ${detail.fireNumber}`);
          return;
        }
        geometry = record.geometry as GeoJSON.Geometry;
      } catch (err) {
        console.error(`[firePerimeterOverlay] Failed to fetch perimeter for ${detail.fireNumber}:`, err);
        return;
      }
    }
    addOverlay(_activeMap, key, geometry, flyTo);
    // Forward the resolved geometry so FirePerimeterLayerSync can cache it
    // and re-shows from the Layer Drawer redraw the exact same polygon
    // (current or historic) without falling through to getCurrentPerimeter.
    window.dispatchEvent(new CustomEvent('fire-perimeter-shown', { detail: { fireNumber: detail.fireNumber, perimeterId: detail.perimeterId, geometry, label: detail.label, description: detail.description } }));
  };

  const onHide = (ev: Event) => {
    const detail = (ev as CustomEvent<HidePerimeterDetail>).detail;
    if (!detail?.fireNumber || !_activeMap) return;
    removeOverlay(_activeMap, overlayKey(detail));
    window.dispatchEvent(new CustomEvent('fire-perimeter-hidden', { detail: { fireNumber: detail.fireNumber, perimeterId: detail.perimeterId } }));
  };

  const onToggle = (ev: Event) => {
    const detail = (ev as CustomEvent<ShowPerimeterDetail>).detail;
    if (!detail?.fireNumber || !_activeMap) return;
    if (isPerimeterShown(detail)) {
      removeOverlay(_activeMap, overlayKey(detail));
      window.dispatchEvent(new CustomEvent('fire-perimeter-hidden', { detail: { fireNumber: detail.fireNumber, perimeterId: detail.perimeterId } }));
    } else {
      onShow(ev);
    }
  };

  window.addEventListener('show-fire-perimeter', onShow);
  window.addEventListener('hide-fire-perimeter', onHide);
  window.addEventListener('toggle-fire-perimeter', onToggle);
}
